/**
 * 共享 Markdown 渲染器
 *
 * 基于 marked v15 + highlight.js + KaTeX 懒加载。
 * 代码块样式来自 mateclaw 同款方案：header（语言标签 + 行数 + Copy 按钮）、
 * CSS 计数器行号、超 20 行/大 JSON 自动折叠。
 * 与会话页面保持一致：直接返回 marked 解析结果，不经过 DOMPurify。
 *
 * 调用方式：
 *   import { renderMarkdownText } from '@/utils/markdownRenderer'
 *   const html = renderMarkdownText(text)
 */
import { Marked } from 'marked'
import hljs from 'highlight.js'
import mermaid from 'mermaid'

// ---------------------------------------------------------------------------
// 语言名称映射
// ---------------------------------------------------------------------------
export const LANG_DISPLAY = {
  js: 'JavaScript', javascript: 'JavaScript', ts: 'TypeScript', typescript: 'TypeScript',
  py: 'Python', python: 'Python', java: 'Java', kt: 'Kotlin', kotlin: 'Kotlin',
  go: 'Go', rust: 'Rust', rs: 'Rust', rb: 'Ruby', ruby: 'Ruby',
  cpp: 'C++', c: 'C', cs: 'C#', csharp: 'C#', swift: 'Swift',
  sh: 'Shell', bash: 'Bash', zsh: 'Zsh', shell: 'Shell',
  sql: 'SQL', html: 'HTML', css: 'CSS', scss: 'SCSS', less: 'LESS',
  json: 'JSON', xml: 'XML', yaml: 'YAML', yml: 'YAML', toml: 'TOML',
  md: 'Markdown', markdown: 'Markdown', dockerfile: 'Dockerfile',
  vue: 'Vue', jsx: 'JSX', tsx: 'TSX', php: 'PHP', lua: 'Lua'
}

const KNOWN_LANGS = [
  'typescript', 'javascript', 'python', 'kotlin', 'csharp', 'dockerfile',
  'markdown', 'shell', 'swift', 'rust', 'ruby', 'bash', 'scss', 'less',
  'yaml', 'toml', 'html', 'java', 'json', 'css', 'cpp', 'xml', 'vue',
  'jsx', 'tsx', 'php', 'lua', 'sql', 'zsh', 'yml', 'go', 'kt', 'rs',
  'rb', 'cs', 'ts', 'js', 'py', 'sh', 'md', 'c'
]

function extractLang(raw) {
  if (!raw) return ''
  const lower = raw.toLowerCase()
  if (hljs.getLanguage(lower)) return lower
  for (const lang of KNOWN_LANGS) {
    if (lower.startsWith(lang) && lower.length > lang.length) return lang
  }
  return lower
}

function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

// ---------------------------------------------------------------------------
// 折叠阈值
// ---------------------------------------------------------------------------
const COLLAPSE_LINE_THRESHOLD = 20
const COLLAPSE_JSON_CHAR_THRESHOLD = 800

// ---------------------------------------------------------------------------
// 链接安全
// ---------------------------------------------------------------------------
const SAFE_LINK_RE = /^(https?:|mailto:|#|\/|\.\/|\.\.\/)/i

// ---------------------------------------------------------------------------
// LaTeX 预处理器（跳过 fenced code 和 inline code）
// ---------------------------------------------------------------------------
function preprocessLatex(text) {
  let out = ''
  let i = 0
  let inFence = false
  let fenceMarker = ''
  while (i < text.length) {
    if (i === 0 || text[i - 1] === '\n') {
      const fenceMatch = /^(```+|~~~+)([^\n]*)/.exec(text.slice(i))
      if (fenceMatch) {
        const marker = fenceMatch[1]
        if (!inFence) {
          inFence = true
          fenceMarker = marker
        } else if (marker.length >= fenceMarker.length && marker[0] === fenceMarker[0]) {
          inFence = false
          fenceMarker = ''
        }
        out += fenceMatch[0]
        i += fenceMatch[0].length
        continue
      }
    }
    if (inFence) { out += text[i++]; continue }

    if (text[i] === '`') {
      let n = 0
      while (text[i + n] === '`') n++
      const tickRun = '`'.repeat(n)
      const close = text.indexOf(tickRun, i + n)
      if (close < 0) { out += text[i++]; continue }
      out += text.slice(i, close + n)
      i = close + n
      continue
    }

    // \[...\]
    if (text[i] === '\\' && text[i + 1] === '[') {
      const close = text.indexOf('\\]', i + 2)
      if (close > 0 && close - i < 800) {
        const tex = text.slice(i + 2, close)
        out += `\n\n<div class="katex-block" data-tex="${encodeURIComponent(tex)}"></div>\n\n`
        i = close + 2
        continue
      }
    }
    // \(...\)
    if (text[i] === '\\' && text[i + 1] === '(') {
      const close = text.indexOf('\\)', i + 2)
      if (close > 0 && close - i < 400) {
        const tex = text.slice(i + 2, close)
        out += `<span class="katex-inline" data-tex="${encodeURIComponent(tex)}"></span>`
        i = close + 2
        continue
      }
    }
    // $$...$$
    if (text[i] === '$' && text[i + 1] === '$') {
      const close = text.indexOf('$$', i + 2)
      if (close > 0) {
        const tex = text.slice(i + 2, close)
        out += `\n\n<div class="katex-block" data-tex="${encodeURIComponent(tex)}"></div>\n\n`
        i = close + 2
        continue
      }
    }
    // $...$
    if (text[i] === '$') {
      const m = /^\$([^$\n]+?)\$(?!\d)/.exec(text.slice(i))
      if (m && !/^\s/.test(m[1]) && !/\s$/.test(m[1])) {
        const tex = m[1]
        out += `<span class="katex-inline" data-tex="${encodeURIComponent(tex)}"></span>`
        i += m[0].length
        continue
      }
    }
    out += text[i++]
  }
  return out
}

// ---------------------------------------------------------------------------
// ECharts fence 配置提取 —— 与客户端 MarkdownChartBlock.tsx 对齐
// 支持三种格式：
//   1) 嵌套 props:  {"component":"chart","props":{"option":{...}}}
//   2) 扁平 option:  {"component":"chart","option":{...}}
//   3) 嵌套 title+option: {"component":"chart","title":"...","option":{...}}
// 返回 ECharts option 对象，或 null
// ---------------------------------------------------------------------------
function _extractChartOption(cfg) {
  if (!cfg || typeof cfg !== 'object') return null

  // 1) 优先从 props.option 提取
  var opt = null
  if (cfg.props && cfg.props.option !== undefined) {
    opt = cfg.props.option
  } else if (cfg.option !== undefined) {
    opt = cfg.option
  } else {
    return null
  }

  // option 可能是 JSON 字符串
  if (typeof opt === 'string') {
    try { opt = JSON.parse(opt) } catch (e) { return null }
  }
  if (!opt || typeof opt !== 'object') return null

  // 2) 处理嵌套 { title, option } 结构
  if (opt.option && typeof opt.option === 'object' && !Array.isArray(opt.option)) {
    var inner = opt.option
    if (opt.title && !inner.title) inner.title = opt.title
    opt = inner
  }

  // 3) 顶层 markLine 合并到所有 series
  if (opt.markLine && Array.isArray(opt.series)) {
    var ml = opt.markLine
    delete opt.markLine
    opt.series = opt.series.map(function (s) {
      return s.markLine ? s : Object.assign({}, s, { markLine: ml })
    })
  }

  return opt
}

// ---------------------------------------------------------------------------
// 自定义 renderer
// ---------------------------------------------------------------------------
const customRenderer = {
  code({ text, lang }) {
    const rawCode = text || ''
    const infoStr = (lang || '').split(/\s/)[0]

    // Mermaid: 输出纯图表占位容器（不显示 code block header 工具栏），由全局 MutationObserver / 手动扫描后渲染 SVG
    if (infoStr === 'mermaid') {
      const encoded = encodeURIComponent(rawCode)
      return '<div class="mermaid-block" data-mermaid="' + encoded + '">' +
        '<div class="mermaid-block__body"></div></div>'
    }

    // ECharts fence 分发：拦截 json fence 中 {"component":"chart", ...} 格式
    // 普通 JSON 代码块 → try 解析失败或无 component:'chart' → 正常 hljs 高亮回退
    if (infoStr === 'json') {
      try {
        var cfg = JSON.parse(rawCode)
        if (cfg && typeof cfg === 'object' && cfg.component === 'chart') {
          var chartOpt = _extractChartOption(cfg)
          if (chartOpt) {
            var chartEncoded = encodeURIComponent(JSON.stringify(chartOpt))
            return '<div class="md-echarts-block" data-chart-escaped="' + chartEncoded + '">' +
              '<div class="md-echarts-block__body" style="min-height:280px;"></div></div>'
          }
        }
      } catch (_e) { /* 普通 JSON，走下面正常 hljs 高亮 */ }
    }

    const detectedLang = extractLang(infoStr)
    const hasLanguage = !!detectedLang && !!hljs.getLanguage(detectedLang)

    let highlighted
    if (!infoStr) {
      // 无语言标签：纯文本代码块，不做语法高亮
      highlighted = escapeHtml(rawCode)
    } else {
      try {
        highlighted = hasLanguage
          ? hljs.highlight(rawCode, { language: detectedLang }).value
          : hljs.highlightAuto(rawCode).value
      } catch {
        highlighted = escapeHtml(rawCode)
      }
    }

    const langLabel = detectedLang ? (LANG_DISPLAY[detectedLang] || detectedLang) : 'Text'
    const encodedCode = encodeURIComponent(rawCode)
    const langClass = hasLanguage ? ' language-' + detectedLang : ''

    const rawLines = highlighted.split('\n')
    if (rawLines.length && rawLines[rawLines.length - 1] === '') rawLines.pop()
    const lineCount = rawLines.length || 1
    const linesHtml = '<ol class="hljs-lines">' + rawLines.map(function (l) { return '<li>' + (l || ' ') + '</li>' }).join('') + '</ol>'

    const isJson = detectedLang === 'json'
    const isLongJson = isJson && rawCode.length >= COLLAPSE_JSON_CHAR_THRESHOLD
    const shouldCollapse = lineCount >= COLLAPSE_LINE_THRESHOLD || isLongJson
    const openByDefault = !isLongJson

    const headerInner = '<span class="code-block__lang">' + escapeHtml(langLabel) + '</span>' +
      '<span class="code-block__lines">' + lineCount + ' lines</span>' +
      '<button class="code-block__copy" type="button" data-code="' + encodedCode + '" aria-label="Copy code">' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>' +
      '<span class="code-block__copy-text">Copy</span>' +
      '</button>'

    const codeBody = '<pre><code class="hljs' + langClass + '">' + linesHtml + '</code></pre>'

    if (shouldCollapse) {
      const openAttr = openByDefault ? ' open' : ''
      return '<details class="code-block code-block--collapsible"' + openAttr + '>' +
        '<summary class="code-block__header">' + headerInner + '</summary>' +
        codeBody +
        '</details>'
    }
    return '<div class="code-block">' +
      '<div class="code-block__header">' + headerInner + '</div>' +
      codeBody +
      '</div>'
  },

  /* listitem 使用 marked 内置 GFM 默认渲染，彻底避免 parseInline "list type not found" 兼容问题。
     marked gfm:true 对 task list 自动输出 <li class="task-list-item"><input type="checkbox" class="task-list-item-checkbox" checked? disabled> ... </li> */

  link: function (linkToken) {
    var href = linkToken.href
    var title = linkToken.title
    var tokens = linkToken.tokens
    var innerHtml = this.parser.parseInline(tokens)

    if (!href || !SAFE_LINK_RE.test(href)) { return innerHtml }

    // 剥离 LLM 幻觉 host
    var hostStripped = /^https?:\/\/[^/]+(\/api\/v1\/files\/generated\/.+)$/i.exec(href)
    var safeHref = hostStripped ? hostStripped[1] : href

    var extra = ''
    try {
      var url = new URL(safeHref, typeof window !== 'undefined' ? window.location.href : 'http://localhost/')
      if (typeof window !== 'undefined' && url.origin !== window.location.origin) {
        extra = ' target="_blank" rel="noopener noreferrer"'
      }
    } catch (e) { /* malformed URL, treat as same-origin */ }

    var titleAttr = title ? ' title="' + escapeHtml(title) + '"' : ''
    return '<a href="' + escapeHtml(safeHref) + '"' + titleAttr + extra + '>' + innerHtml + '</a>'
  }
}

// ---------------------------------------------------------------------------
// marked 实例（独立实例，不影响全局 marked/会话页面）
// 通过 markedInstance.use() 扩展 tokenizer 和 renderer 方法，
// 保留 listitem/checkbox 等 GFM 默认渲染方法
// ---------------------------------------------------------------------------
const markedInstance = new Marked({
  gfm: true,
  breaks: true
})

markedInstance.use({
  tokenizer: {
    del: function (src) {
      var cap = /^~~([^~]+?)~~/.exec(src)
      if (cap) {
        return {
          type: 'del',
          raw: cap[0],
          text: cap[1],
          tokens: this.lexer.inlineTokens(cap[1])
        }
      }
      return undefined
    }
  }
})

markedInstance.use({
  renderer: {
    code: customRenderer.code,
    link: customRenderer.link,
    del: function (token) {
      return '<del>' + this.parser.parseInline(token.tokens) + '</del>'
    }
  }
})

// ---------------------------------------------------------------------------
// LRU 渲染缓存
// ---------------------------------------------------------------------------
var RENDER_CACHE = new Map()
var RENDER_CACHE_CAP = 200
var RENDER_CACHE_VERSION = 'v11' // v11: 新增 echarts fence 分发

function cacheKey(text) {
  return RENDER_CACHE_VERSION + ':' + text.length + ':' + text.slice(0, 40) + ':' + text.slice(-40)
}

// ---------------------------------------------------------------------------
// 飞书同步消息中的 [附件] 标记处理
// 格式: [附件]文件名.pdf (路径:/app/data/chat-uploads/xxx/文件名.pdf)
// ---------------------------------------------------------------------------
var ATTACHMENT_MARKER_REGEX = /\[附件\](.+?)\s*\(路径:([^)]+)\)/g

/**
 * 将用户纯文本消息渲染为安全 HTML，并把 [附件]文件名(路径:xxx) 转成文件超链接。
 * 用于用户气泡（原本为 {{ text }} 纯文本插值，不经过 markdown）。
 * 先整体 HTML 转义防 XSS，再替换附件标记。
 */
export function renderUserTextWithAttachments(content) {
  if (!content) return ''
  var escaped = escapeHtml(content)
  return escaped.replace(ATTACHMENT_MARKER_REGEX, function(_m, fileName, filePath) {
    return '<a class="chat-attachment" href="' + filePath + '" target="_blank" rel="noopener noreferrer">' +
      '<i class="el-icon-document"></i>' + fileName + '</a>'
  })
}

/**
 * 纯文本场景（如左侧会话列表标题）：
 * 1. 完整格式 [附件]文件名(路径:xxx) → 📄 文件名
 * 2. 残留的孤立 [附件] 前缀（路径缺失或标题被后端截断）→ 直接去掉
 */
export function stripAttachmentMarker(text) {
  if (!text) return text
  return text
    .replace(ATTACHMENT_MARKER_REGEX, '📄 $1')
    .replace(/\[附件\]/g, '')
    .replace(/\s*[（(]\s*路径\s*[:：][^)）\n]*(?:[)）]|$)/gm, '')
}

export function renderMarkdownText(content) {
  if (!content) return ''

  var k = cacheKey(content)
  var cached = RENDER_CACHE.get(k)
  if (cached !== undefined) {
    RENDER_CACHE.delete(k)
    RENDER_CACHE.set(k, cached)
    return cached
  }

  // 1. LaTeX 占位（跳过 fenced code）
  var withLatex = preprocessLatex(content)

  // 2. marked 解析（与会话页面一致，不经过 DOMPurify）
  var result = markedInstance.parse(withLatex)

  // 2.5 [附件]文件名 (路径:xxx) → 文件图标 + 超链接
  result = result.replace(
    /\[附件\]([^\s(]+)\s*\(路径:([^)]+)\)/g,
    function(_, fileName, filePath) {
      return '<a class="chat-attachment" href="' + filePath + '" target="_blank"><i class="el-icon-document"></i>' + fileName + '</a>'
    }
  )

  // 3. LRU 淘汰
  if (RENDER_CACHE.size >= RENDER_CACHE_CAP) {
    var oldestKey = RENDER_CACHE.keys().next().value
    if (oldestKey !== undefined) RENDER_CACHE.delete(oldestKey)
  }
  RENDER_CACHE.set(k, result)
  return result
}

// ---------------------------------------------------------------------------
// Mermaid 全局初始化 + 自动渲染 + Task list 兜底 (MutationObserver)
// 不管哪个组件 / 页面调用 renderMarkdownText，只要内容插入到 DOM，
// MutationObserver 就会自动检测并:
//   1) 把 .mermaid-block 的 data-mermaid 编码内容渲染成 SVG
//   2) 给勾选状态的 li.task-list-item 追加 task-list-item--checked 类
//      (不支持 CSS :has() 的浏览器兜底)
// ---------------------------------------------------------------------------
var _mermaidInited = false
function _ensureMermaidInit() {
  if (_mermaidInited) return
  try {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'loose',
      flowchart: { useMaxWidth: true, htmlLabels: true, curve: 'basis' },
      sequence: { useMaxWidth: true, showSequenceNumbers: false },
      gantt: { useMaxWidth: true }
    })
    _mermaidInited = true
  } catch (e) {
    console.warn('[markdownRenderer] mermaid.initialize failed:', e)
  }
}

function _renderSingleMermaid(block) {
  try {
    if (!block || block.dataset.mermaidRendered === '1') return
    const encoded = block.getAttribute('data-mermaid')
    if (!encoded) return
    let code = ''
    try { code = decodeURIComponent(encoded) } catch (e) { code = encoded }
    if (!code.trim()) return
    const body = block.querySelector('.mermaid-block__body')
    if (!body) return
    _ensureMermaidInit()
    const id = 'mmd-' + Math.random().toString(36).slice(2, 10)
    mermaid.render(id, code).then(function (res) {
      if (res && res.svg) {
        body.innerHTML = res.svg
        if (res.bindFunctions) try { res.bindFunctions(body) } catch (_e) { /* mermaid bindFunctions 失败不影响 SVG 展示 */ }
      } else {
        body.innerHTML = '<div class="mermaid-error">图表渲染失败</div>'
      }
      block.dataset.mermaidRendered = '1'
    }).catch(function (err) {
      console.warn('[markdownRenderer] mermaid.render failed:', err)
      body.innerHTML = '<div class="mermaid-error">图表渲染失败: ' + (err && err.message ? err.message : '') + '</div>'
      block.dataset.mermaidRendered = '1'
    })
  } catch (e) {
    console.warn('[markdownRenderer] _renderSingleMermaid exception:', e)
  }
}

function _fixupTaskListChecked(root) {
  try {
    if (!root || !root.querySelectorAll) return
    // 已勾选的 task item 追加兜底类名 (不支持 :has() 的浏览器)
    const items = root.querySelectorAll('li.task-list-item')
    for (let i = 0; i < items.length; i++) {
      const li = items[i]
      if (li.classList.contains('task-list-item--checked')) continue
      const input = li.querySelector('input.task-list-item-checkbox')
      if (input && input.checked) li.classList.add('task-list-item--checked')
    }
  } catch (e) { /* noop */ }
}

// ---------------------------------------------------------------------------
// ECharts fence 渲染 + 实例管理
// 动态 import echarts（避免所有页面都加载几 MB 的 echarts）
// ---------------------------------------------------------------------------
var _echartsModule = null
var _echartsLoading = null
var _echartsInstances = new WeakMap() // HTMLElement → echarts instance

function _getEchartsModule() {
  if (_echartsModule) return Promise.resolve(_echartsModule)
  if (_echartsLoading) return _echartsLoading
  _echartsLoading = import('echarts').then(function (mod) {
    _echartsModule = mod
    _echartsLoading = null
    return mod
  }).catch(function (e) {
    _echartsLoading = null
    console.warn('[markdownRenderer] echarts dynamic import failed:', e)
    return null
  })
  return _echartsLoading
}

function _disposeEchartsForBlock(block) {
  try {
    var old = _echartsInstances.get(block)
    if (old) { old.dispose(); _echartsInstances.delete(block) }
  } catch (e) { /* noop */ }
}

function _renderSingleEcharts(block) {
  try {
    if (!block || block.dataset.echartsRendered === '1') return
    var encoded = block.getAttribute('data-chart-escaped')
    if (!encoded) return
    var opt
    try { opt = JSON.parse(decodeURIComponent(encoded)) } catch (e) {
      console.warn('[markdownRenderer] echarts option parse failed')
      return
    }
    if (!opt || typeof opt !== 'object') return
    var body = block.querySelector('.md-echarts-block__body')
    if (!body) return

    // 确保 body 有可见尺寸 —— 用 rAF 等布局完成
    requestAnimationFrame(function () {
      _getEchartsModule().then(function (echarts) {
        if (!echarts) return
        try {
          // 再次检查（rendered 标记 + DOM 还在不在）
          if (block.dataset.echartsRendered === '1') return
          if (!block.isConnected) return

          // dispose 旧实例（如果有）
          _disposeEchartsForBlock(block)

          // body 宽高兜底
          var w = body.offsetWidth || 600
          var h = body.offsetHeight || 280
          if (w < 50) w = 600
          if (h < 50) h = 280

          var inst = echarts.init(body, null, { renderer: 'canvas' })
          inst.setOption(opt, true)
          inst.resize({ width: w, height: h })
          _echartsInstances.set(block, inst)
          block.dataset.echartsRendered = '1'

          // 窗口 resize 时 resize 图表（用一次性监听，组件销毁时自动 GC）
          var resizeHandler = function () {
            if (!block.isConnected) {
              window.removeEventListener('resize', resizeHandler)
              _disposeEchartsForBlock(block)
              return
            }
            try { inst.resize() } catch (_e) { /* noop */ }
          }
          window.addEventListener('resize', resizeHandler)
        } catch (e) {
          console.warn('[markdownRenderer] echarts render failed:', e)
          body.innerHTML = '<div class="echarts-error" style="color:#e5484d;padding:8px;">图表渲染失败: ' +
            (e && e.message ? e.message : '') + '</div>'
          block.dataset.echartsRendered = '1'
        }
      })
    })
  } catch (e) {
    console.warn('[markdownRenderer] _renderSingleEcharts exception:', e)
  }
}

function _scanAndProcess(root) {
  const workRoot = (root.nodeType === 1 || root.nodeType === 9 || root.nodeType === 11) ? root : document
  // 1) mermaid: 先处理当前元素本身, 再处理后代
  if (workRoot.classList && workRoot.classList.contains('mermaid-block')) {
    _renderSingleMermaid(workRoot)
  }
  try {
    const blocks = workRoot.querySelectorAll ? workRoot.querySelectorAll('.mermaid-block') : []
    for (let i = 0; i < blocks.length; i++) _renderSingleMermaid(blocks[i])
  } catch (e) { /* noop */ }
  // 2) echarts fence: 当前元素 + 后代
  if (workRoot.classList && workRoot.classList.contains('md-echarts-block')) {
    _renderSingleEcharts(workRoot)
  }
  try {
    var chartBlocks = workRoot.querySelectorAll ? workRoot.querySelectorAll('.md-echarts-block') : []
    for (var ci = 0; ci < chartBlocks.length; ci++) _renderSingleEcharts(chartBlocks[ci])
  } catch (_e) { /* noop */ }
  // 3) task list checked 类名兜底
  _fixupTaskListChecked(workRoot)
}

// 在浏览器环境下启动 MutationObserver
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  _ensureMermaidInit()

  // 启动时立即扫描一次 (模块加载时已有内容的情况)
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(function () { _scanAndProcess(document.body) }, 0)
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      _scanAndProcess(document.body)
    })
  }

  try {
    var mermaidObserver = new MutationObserver(function (mutations) {
      for (let m = 0; m < mutations.length; m++) {
        const mut = mutations[m]
        // 子节点插入 → 扫描渲染（mermaid + echarts + task list）
        if (mut.addedNodes && mut.addedNodes.length) {
          for (let n = 0; n < mut.addedNodes.length; n++) {
            const node = mut.addedNodes[n]
            if (node.nodeType === 1) _scanAndProcess(node)
          }
        }
        // 子节点移除 → dispose echarts 实例（防止内存泄漏）
        if (mut.removedNodes && mut.removedNodes.length) {
          for (let r = 0; r < mut.removedNodes.length; r++) {
            const rNode = mut.removedNodes[r]
            if (rNode.nodeType !== 1) continue
            // 被移除的是 echarts block 自身
            if (rNode.classList && rNode.classList.contains('md-echarts-block')) {
              _disposeEchartsForBlock(rNode)
            }
            // 被移除的父节点里可能有 echarts block 后代
            try {
              var removedCharts = rNode.querySelectorAll('.md-echarts-block')
              for (var rci = 0; rci < removedCharts.length; rci++) {
                _disposeEchartsForBlock(removedCharts[rci])
              }
            } catch (_e) { /* noop */ }
          }
        }
        // 属性变更: mermaid / echarts
        if (mut.type === 'attributes' && mut.target && mut.target.nodeType === 1) {
          if (mut.target.classList && mut.target.classList.contains('mermaid-block')) {
            _renderSingleMermaid(mut.target)
          }
          if (mut.target.classList && mut.target.classList.contains('md-echarts-block')) {
            _renderSingleEcharts(mut.target)
          }
        }
      }
    })
    mermaidObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-mermaid', 'data-chart-escaped', 'class']
    })
  } catch (e) {
    console.warn('[markdownRenderer] MutationObserver init failed:', e)
  }
}

// 导出手动扫描函数 (供特殊场景下主动调用, 例如 Shadow DOM 内容注入后)
export function scanAndRenderMarkdownWidgets(rootEl) {
  _scanAndProcess(rootEl)
}

export { markedInstance }
export default markedInstance
